import { GetPageBySlug } from "../../queries/GetPageBySlug";
import { useParams } from "react-router";
import { Loading } from "./Loading";
import { NotFound } from "./NotFound";
import { Page } from "./Page";
import { useAsyncAbortable } from "@react-hookz/web";
import { contentfulClient } from "../../utils/contentfulClient";
import { useCallback, useEffect } from "react";
import { EntryMap } from "./EntryMap";
import { PageBlockInstance, PageModel } from "../../types/PageModel";
import { useGlobalContext } from "../../utils/GlobalContext";

export const PageResolver = () => {
  // if no slug, we are on the index route, pull homepage
  const { slug = "homepage" } = useParams();
  const { locale, previewContent } = useGlobalContext();

  const getPageData = useCallback(async (): Promise<
    PageModel | { notFound: true }
  > => {
    const pageQueryResponse = await contentfulClient.query(GetPageBySlug, {
      slug,
      locale,
      preview: previewContent,
    });

    const page = pageQueryResponse?.data?.pageCollection?.items[0];

    if (!page) return { notFound: true };

    const entries = await Promise.allSettled(
      [...(page.blocksCollection?.items ?? []), page.hero].map(
        async (block) => {
          if (!block || !block.__typename || !block.sys.id)
            throw new Error("No block found or no data in block");
          const entry = EntryMap[block.__typename];
          if (!entry) throw new Error("No entry found");
          const instance = (
            await contentfulClient.query(entry.query, {
              id: block?.sys.id,
              locale,
              preview: previewContent,
            })
          ).data;

          return { __typename: block.__typename, instance };
        }
      ) as Array<Promise<PageBlockInstance>>
    );

    const blocks = entries
      .filter((entrySettled) => entrySettled.status === "fulfilled")
      .map((entrySettled) => {
        if (entrySettled.status === "fulfilled") return entrySettled.value;
      })
      .filter(Boolean);

    const hero = page.hero ? blocks.pop() : null;

    return { page, hero, blocks };
  }, [slug, locale, previewContent]);
  const [pageData, actions] = useAsyncAbortable(getPageData);
  useEffect(() => {
    actions.execute();
    return () => actions.abort();
  }, [getPageData, actions]);

  if (pageData.status === "loading" || !pageData.result) {
    return <Loading />;
  }

  if ("notFound" in pageData.result) {
    return <NotFound />;
  }

  return <Page key={slug} page={pageData.result} />;
};
